/**
 * SocialAccountsManager Component
 * ================================
 * Phase 8A: Manage connected social media accounts
 * - Connect/disconnect accounts
 * - Test connections
 * - View connection status
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Link2, Unlink, RefreshCcw, CheckCircle, XCircle, AlertTriangle,
  ExternalLink, Plus, Trash2, Loader2, Shield, Clock, Settings, HelpCircle, Copy, Check
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Platform configurations with setup instructions
const PLATFORMS = [
  { 
    key: 'facebook', 
    name: 'Facebook', 
    icon: '📘',
    color: 'bg-blue-500',
    description: 'Connect Facebook Pages to publish posts',
    devPortalUrl: 'https://developers.facebook.com/',
    envVars: ['FACEBOOK_CLIENT_ID', 'FACEBOOK_CLIENT_SECRET'],
    setupSteps: [
      'Go to Meta Developer Console and create a new app',
      'Select "Business" as your app type',
      'Add the "Facebook Login" product to your app',
      'In Facebook Login Settings, add the OAuth Redirect URI',
      'Go to App Settings > Basic to get your App ID and App Secret',
      'Request the required permissions: pages_manage_posts, pages_read_engagement',
      'Submit your app for review to get production access'
    ],
    permissions: ['pages_manage_posts', 'pages_read_engagement', 'pages_show_list'],
    callbackPath: '/api/social/callback/facebook'
  },
  { 
    key: 'instagram', 
    name: 'Instagram', 
    icon: '📷',
    color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    description: 'Connect Instagram Business accounts',
    devPortalUrl: 'https://developers.facebook.com/',
    envVars: ['FACEBOOK_CLIENT_ID', 'FACEBOOK_CLIENT_SECRET'],
    setupSteps: [
      'Instagram uses Facebook OAuth - use the same Facebook app',
      'Your Instagram account must be a Business or Creator account',
      'Link your Instagram account to a Facebook Page',
      'Add Instagram permissions to your Facebook app',
      'Request permissions: instagram_basic, instagram_content_publish',
      'Submit for App Review with Instagram permissions'
    ],
    permissions: ['instagram_basic', 'instagram_content_publish', 'pages_read_engagement'],
    callbackPath: '/api/social/callback/instagram',
    note: 'Instagram uses Facebook OAuth. Use your Facebook App credentials.'
  },
  { 
    key: 'linkedin', 
    name: 'LinkedIn', 
    icon: '💼',
    color: 'bg-blue-700',
    description: 'Connect LinkedIn Company Pages',
    devPortalUrl: 'https://www.linkedin.com/developers/',
    envVars: ['LINKEDIN_CLIENT_ID', 'LINKEDIN_CLIENT_SECRET'],
    setupSteps: [
      'Go to LinkedIn Developer Portal and create a new app',
      'Fill in your company details and app information',
      'Go to the "Auth" tab to find your Client ID and Client Secret',
      'Add the OAuth 2.0 Redirect URL in the Auth settings',
      'Go to "Products" tab and request access to:',
      '  • Sign In with LinkedIn using OpenID Connect',
      '  • Share on LinkedIn',
      'Wait for product access approval (usually instant for Sign In)'
    ],
    permissions: ['openid', 'profile', 'email', 'w_member_social'],
    callbackPath: '/api/social/callback/linkedin'
  },
  { 
    key: 'twitter', 
    name: 'Twitter/X', 
    icon: '🐦',
    color: 'bg-black',
    description: 'Connect Twitter accounts',
    devPortalUrl: 'https://developer.twitter.com/',
    envVars: ['TWITTER_CLIENT_ID', 'TWITTER_CLIENT_SECRET'],
    setupSteps: [
      'Go to Twitter Developer Portal and create a project',
      'Create an app within your project',
      'Go to "Keys and tokens" to generate your API keys',
      'In "User authentication settings", enable OAuth 2.0',
      'Set App permissions to "Read and write"',
      'Add your Callback URL / Redirect URI',
      'Copy your Client ID and Client Secret'
    ],
    permissions: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
    callbackPath: '/api/social/callback/twitter',
    note: 'Requires Twitter API v2 access (Free tier available)'
  },
  { 
    key: 'google_business', 
    name: 'Google Business', 
    icon: '📍',
    color: 'bg-green-500',
    description: 'Connect Google Business Profile',
    devPortalUrl: 'https://console.cloud.google.com/',
    envVars: ['GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_SECRET'],
    setupSteps: [
      'Go to Google Cloud Console and create a new project',
      'Enable the "My Business Account Management API"',
      'Go to "APIs & Services" > "Credentials"',
      'Create an OAuth 2.0 Client ID (Web application type)',
      'Add your Authorized redirect URI',
      'Download or copy your Client ID and Client Secret',
      'Configure the OAuth consent screen with required scopes'
    ],
    permissions: ['https://www.googleapis.com/auth/business.manage'],
    callbackPath: '/api/social/callback/google_business'
  }
];

const STATUS_CONFIG = {
  connected: { label: 'Connected', color: 'bg-green-500', icon: CheckCircle },
  disconnected: { label: 'Disconnected', color: 'bg-slate-500', icon: Unlink },
  expired: { label: 'Token Expired', color: 'bg-yellow-500', icon: AlertTriangle },
  error: { label: 'Error', color: 'bg-red-500', icon: XCircle }
};

// Account Card Component
const AccountCard = ({ account, onTest, onDisconnect, testing }) => {
  const statusConfig = STATUS_CONFIG[account.status] || STATUS_CONFIG.disconnected;
  const StatusIcon = statusConfig.icon;
  const platform = PLATFORMS.find(p => p.key === account.platform);

  return (
    <div className="p-4 rounded-lg border dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{platform?.icon || '🔗'}</span>
          <div>
            <p className="font-medium dark:text-white">{account.account_name}</p>
            <p className="text-xs text-muted-foreground capitalize">{account.account_type}</p>
          </div>
        </div>
        <Badge className={`${statusConfig.color} text-white`}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {statusConfig.label}
        </Badge>
      </div>
      
      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {account.last_verified_at && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Verified {formatDistanceToNow(new Date(account.last_verified_at), { addSuffix: true })}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onTest(account.id)}
            disabled={testing === account.id}
            data-testid={`test-connection-${account.id}`}
          >
            {testing === account.id ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <RefreshCcw className="h-3 w-3 mr-1" />
            )}
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDisconnect(account.id)}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid={`disconnect-${account.id}`}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Disconnect
          </Button>
        </div>
      </div>
      
      {account.error_message && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-700 dark:text-red-400">
          {account.error_message}
        </div>
      )}
    </div>
  );
};

// Main Component
export const SocialAccountsManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [platformsStatus, setPlatformsStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualPlatform, setManualPlatform] = useState(null);
  const [manualForm, setManualForm] = useState({ account_name: '', access_token: '' });
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [helpPlatform, setHelpPlatform] = useState(null);
  const [copiedText, setCopiedText] = useState(null);

  // Get the current app URL for redirect URIs
  const appUrl = window.location.origin;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
    toast.success('Copied to clipboard!');
  };

  const openHelp = (platformKey) => {
    setHelpPlatform(PLATFORMS.find(p => p.key === platformKey));
    setShowHelpDialog(true);
  };

  const getHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch accounts and platform status
  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, platformsRes] = await Promise.all([
        axios.get(`${API}/social/accounts`, { headers: getHeaders() }),
        axios.get(`${API}/social/platforms`, { headers: getHeaders() })
      ]);
      
      setAccounts(accountsRes.data.accounts || []);
      setPlatformsStatus(platformsRes.data.platforms || []);
    } catch (error) {
      console.error('Failed to fetch social data:', error);
      toast.error('Failed to load social accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Connect account via OAuth
  const connectOAuth = async (platform) => {
    try {
      const response = await axios.post(
        `${API}/social/connect/${platform}`,
        { redirect_url: window.location.href },
        { headers: getHeaders() }
      );
      
      if (response.data.auth_url) {
        // Open OAuth popup or redirect
        window.open(response.data.auth_url, '_blank', 'width=600,height=700');
        toast.info(`Complete the ${platform} authorization in the popup window`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to connect ${platform}`);
    }
  };

  // Add manual account
  const addManualAccount = async () => {
    if (!manualForm.account_name || !manualForm.access_token) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(
        `${API}/social/accounts/manual`,
        {
          platform: manualPlatform,
          account_name: manualForm.account_name,
          access_token: manualForm.access_token
        },
        { headers: getHeaders() }
      );
      
      toast.success('Account added successfully');
      setShowManualDialog(false);
      setManualForm({ account_name: '', access_token: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account');
    }
  };

  // Test connection
  const testConnection = async (accountId) => {
    setTesting(accountId);
    try {
      const response = await axios.post(
        `${API}/social/accounts/${accountId}/test`,
        {},
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        toast.success('Connection verified successfully');
        fetchData();
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  // Disconnect account
  const disconnectAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) return;

    try {
      await axios.post(
        `${API}/social/accounts/${accountId}/disconnect`,
        {},
        { headers: getHeaders() }
      );
      
      toast.success('Account disconnected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  // Group accounts by platform
  const accountsByPlatform = accounts.reduce((acc, account) => {
    if (!acc[account.platform]) acc[account.platform] = [];
    acc[account.platform].push(account);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-accounts-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold dark:text-white">Connected Accounts</h3>
          <p className="text-sm text-muted-foreground">
            {accounts.length} account{accounts.length !== 1 ? 's' : ''} connected
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCcw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Platform Cards */}
      <div className="space-y-4">
        {PLATFORMS.map(platform => {
          const status = platformsStatus.find(p => p.platform === platform.key);
          const platformAccounts = accountsByPlatform[platform.key] || [];
          
          return (
            <Card key={platform.key} className="dark:bg-slate-800/60">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center text-xl`}>
                      {platform.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base dark:text-white">{platform.name}</CardTitle>
                      <CardDescription className="text-xs">{platform.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {status?.is_configured ? (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <Shield className="h-3 w-3 mr-1" />
                        Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Not Configured
                      </Badge>
                    )}
                    <Badge variant="secondary">
                      {platformAccounts.length} connected
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Connected Accounts */}
                {platformAccounts.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {platformAccounts.map(account => (
                      <AccountCard
                        key={account.id}
                        account={account}
                        onTest={testConnection}
                        onDisconnect={disconnectAccount}
                        testing={testing}
                      />
                    ))}
                  </div>
                )}

                {/* Connect Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => connectOAuth(platform.key)}
                    disabled={!status?.is_configured}
                    data-testid={`connect-oauth-${platform.key}`}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Connect via OAuth
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setManualPlatform(platform.key);
                      setShowManualDialog(true);
                    }}
                    data-testid={`connect-manual-${platform.key}`}
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Add with API Key
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openHelp(platform.key)}
                    className="text-blue-600 hover:text-blue-700"
                    data-testid={`help-${platform.key}`}
                  >
                    <HelpCircle className="h-3 w-3 mr-1" />
                    Setup Guide
                  </Button>
                </div>

                {!status?.is_configured && (
                  <p className="text-xs text-muted-foreground mt-2">
                    OAuth not configured. Add API credentials in environment variables or use manual API key.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800 dark:text-blue-300">Phase 8A: Account Connections</p>
            <p className="text-blue-700 dark:text-blue-400 mt-1">
              Connect your social accounts to enable publishing. In this phase, you can:
            </p>
            <ul className="text-blue-600 dark:text-blue-400 mt-2 space-y-1 list-disc list-inside">
              <li>Connect accounts via OAuth or API key</li>
              <li>Test connections to verify they work</li>
              <li>Perform dry-run publishes to validate content</li>
            </ul>
            <p className="text-blue-600 dark:text-blue-400 mt-2 text-xs">
              Live publishing will be enabled in Phase 8B after connection stability is verified.
            </p>
          </div>
        </div>
      </div>

      {/* Manual Add Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent>
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              Add {PLATFORMS.find(p => p.key === manualPlatform)?.name} Account
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect this account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Hidden fields to trick browser autofill */}
            <input type="text" style={{display: 'none'}} />
            <input type="password" style={{display: 'none'}} />
            <div>
              <Label>Account Name</Label>
              <Input
                placeholder="e.g., My Business Page"
                value={manualForm.account_name}
                onChange={(e) => setManualForm({...manualForm, account_name: e.target.value})}
                className="mt-1"
                autoComplete="off"
                data-form-type="other"
                data-lpignore="true"
              />
            </div>
            <div>
              <Label>Access Token / API Key</Label>
              <Input
                type="password"
                placeholder="Enter your access token"
                value={manualForm.access_token}
                onChange={(e) => setManualForm({...manualForm, access_token: e.target.value})}
                className="mt-1"
                autoComplete="new-password"
                data-form-type="other"
                data-lpignore="true"
                name="social_api_token"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualDialog(false)}>Cancel</Button>
            <Button onClick={addManualAccount}>Add Account</Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Help/Setup Guide Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {helpPlatform && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${helpPlatform.color} flex items-center justify-center text-xl`}>
                    {helpPlatform.icon}
                  </div>
                  <div>
                    <DialogTitle className="text-xl">
                      {helpPlatform.name} Setup Guide
                    </DialogTitle>
                    <DialogDescription>
                      Follow these steps to connect {helpPlatform.name} to RHINO-1
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Developer Portal Link */}
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-800 dark:text-blue-300">Developer Portal</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">{helpPlatform.devPortalUrl}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(helpPlatform.devPortalUrl, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open
                  </Button>
                </div>

                {/* Note if present */}
                {helpPlatform.note && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      <strong>Note:</strong> {helpPlatform.note}
                    </p>
                  </div>
                )}

                {/* Setup Steps */}
                <div>
                  <h4 className="font-semibold mb-3 dark:text-white flex items-center gap-2">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded text-xs">
                      Step by Step
                    </span>
                    Setup Instructions
                  </h4>
                  <ol className="space-y-2">
                    {helpPlatform.setupSteps.map((step, idx) => (
                      <li key={idx} className="flex gap-3 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-medium">
                          {idx + 1}
                        </span>
                        <span className="text-slate-700 dark:text-slate-300 pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Redirect URI */}
                <div>
                  <h4 className="font-semibold mb-2 dark:text-white">Redirect URI (Callback URL)</h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    Add this URL to your app&apos;s OAuth settings:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-slate-100 dark:bg-slate-900 rounded text-sm font-mono overflow-x-auto">
                      {appUrl}{helpPlatform.callbackPath}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(`${appUrl}${helpPlatform.callbackPath}`, 'redirect')}
                    >
                      {copiedText === 'redirect' ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Environment Variables */}
                <div>
                  <h4 className="font-semibold mb-2 dark:text-white">Environment Variables</h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    Add these to your backend .env file:
                  </p>
                  <div className="space-y-2">
                    {helpPlatform.envVars.map((envVar) => (
                      <div key={envVar} className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-slate-100 dark:bg-slate-900 rounded text-sm font-mono">
                          {envVar}=your_value_here
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(envVar, envVar)}
                        >
                          {copiedText === envVar ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Required Permissions/Scopes */}
                <div>
                  <h4 className="font-semibold mb-2 dark:text-white">Required Permissions/Scopes</h4>
                  <div className="flex flex-wrap gap-2">
                    {helpPlatform.permissions.map((perm) => (
                      <Badge key={perm} variant="secondary" className="font-mono text-xs">
                        {perm}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Alternative: Manual API Key */}
                <div className="border-t dark:border-slate-700 pt-4">
                  <h4 className="font-semibold mb-2 dark:text-white">Alternative: Use API Key Directly</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    If you already have an access token or API key, you can add it directly using the &quot;Add with API Key&quot; button without configuring OAuth.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowHelpDialog(false);
                      setManualPlatform(helpPlatform.key);
                      setShowManualDialog(true);
                    }}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Add with API Key
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowHelpDialog(false)}>
                  Close
                </Button>
                <Button onClick={() => window.open(helpPlatform.devPortalUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Go to {helpPlatform.name} Developer Portal
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialAccountsManager;
